import {FC, Fragment, useCallback, useEffect, useState} from 'react'
import {
  getAffiliatePayouts,
  markAffiliatePaid,
  unmarkAffiliatePaid,
  AffiliatePayoutRow,
  AffiliatePayoutsResponse,
} from './_requests'

const nis = (n: number) => '₪' + Math.round(n).toLocaleString('he-IL')

const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return `${HE_MONTHS[m - 1]} ${y}`
}

const dateLabel = (iso: string) => {
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

const card = (bg: string): React.CSSProperties => ({
  background: bg,
  borderRadius: 12,
  padding: '18px 20px',
  color: '#fff',
  flex: '1 1 180px',
  minWidth: 180,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
})

const AffiliatePayoutsPage: FC = () => {
  const [data, setData] = useState<AffiliatePayoutsResponse | null>(null)
  const [month, setMonth] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState<string | null>(null)

  const load = useCallback(async (m?: string) => {
    setLoading(true)
    setError('')
    try {
      const {data} = await getAffiliatePayouts(m ? {month: m} : undefined)
      setData(data)
      setMonth(data.month)
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה בטעינת הנתונים')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggle = (id: string) => setExpanded((s) => ({...s, [id]: !s[id]}))

  const doMarkPaid = async (row: AffiliatePayoutRow) => {
    if (!data) return
    if (!window.confirm(`לסמן ששולם ל${row.affiliate_name} עבור ${monthLabel(data.month)}?\n${row.count} חיבורים · ${nis(row.amount)}`)) return
    setSaving(row.affiliate_id)
    try {
      await markAffiliatePaid({affiliate_id: row.affiliate_id, month: data.month})
      await load(data.month)
    } catch (e: any) {
      alert(e?.response?.data?.error || e.message || 'שמירה נכשלה')
    } finally {
      setSaving(null)
    }
  }

  const doUnmarkPaid = async (row: AffiliatePayoutRow) => {
    if (!data) return
    if (!window.confirm(`לבטל את סימון התשלום ל${row.affiliate_name} עבור ${monthLabel(data.month)}?`)) return
    setSaving(row.affiliate_id)
    try {
      await unmarkAffiliatePaid({affiliate_id: row.affiliate_id, month: data.month})
      await load(data.month)
    } catch (e: any) {
      alert(e?.response?.data?.error || e.message || 'ביטול נכשל')
    } finally {
      setSaving(null)
    }
  }

  const totals = data?.totals
  const rate = data?.rate || 50

  return (
    <div style={{direction: 'rtl', padding: '24px 28px', maxWidth: 1100, margin: '0 auto', fontFamily: 'inherit'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 6}}>
        <div>
          <h1 style={{fontSize: 26, fontWeight: 700, margin: 0}}>תשלום לאפילייטים — חיבורי פייסבוק</h1>
          <div style={{color: '#6b7280', marginTop: 4, fontSize: 14}}>
            {nis(rate)} עבור כל לקוח שחובר לפייסבוק. חודש החיבור נקבע לפי מועד יצירת פרופיל הפייסבוק המחובר של הלקוח.
          </div>
        </div>
        {data && (
          <select
            value={month}
            onChange={(e) => load(e.target.value)}
            style={{padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, fontWeight: 600, background: '#fff', minWidth: 160}}
          >
            {data.available_months.map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{padding: 60, textAlign: 'center'}}>
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : error ? (
        <div style={{background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: 16, marginTop: 16}}>{error}</div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18}}>
            <div style={card('linear-gradient(135deg,#111827,#374151)')}>
              <div style={{fontSize: 13, opacity: 0.85}}>סה״כ לתשלום · {monthLabel(month)}</div>
              <div style={{fontSize: 30, fontWeight: 800, marginTop: 2}}>{nis(totals?.owed || 0)}</div>
              <div style={{fontSize: 12, opacity: 0.8, marginTop: 4}}>{totals?.connections || 0} חיבורים · {totals?.affiliates || 0} אפילייטים</div>
            </div>
            <div style={card('linear-gradient(135deg,#d97706,#f59e0b)')}>
              <div style={{fontSize: 13, opacity: 0.85}}>ממתין לתשלום</div>
              <div style={{fontSize: 30, fontWeight: 800, marginTop: 2}}>{nis(totals?.unpaid || 0)}</div>
            </div>
            <div style={card('linear-gradient(135deg,#059669,#10b981)')}>
              <div style={{fontSize: 13, opacity: 0.85}}>שולם</div>
              <div style={{fontSize: 30, fontWeight: 800, marginTop: 2}}>{nis(totals?.paid || 0)}</div>
            </div>
            {/* What the referred customers have actually paid us — the other side
                of the ledger from what we owe the affiliates for bringing them. */}
            <div style={card('linear-gradient(135deg,#1d4ed8,#3b82f6)')}>
              <div style={{fontSize: 13, opacity: 0.85}}>הכנסות מהלקוחות המשויכים</div>
              <div style={{fontSize: 30, fontWeight: 800, marginTop: 2}}>{nis(totals?.revenue_all_time || 0)}</div>
              <div style={{fontSize: 12, opacity: 0.8, marginTop: 4}}>
                {nis(totals?.revenue_all_month || 0)} התקבלו ב{monthLabel(month)} · {totals?.paying_customers || 0} לקוחות משלמים
              </div>
            </div>
          </div>

          {/* Per-affiliate table */}
          <div style={{marginTop: 24, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden'}}>
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720}}>
                <thead>
                  <tr style={{background: '#f9fafb', textAlign: 'right', color: '#6b7280'}}>
                    <th style={th}>אפילייט</th>
                    <th style={th}>טלפון</th>
                    <th style={th}>חיבורים</th>
                    <th style={{...th, color: '#111827'}}>לתשלום</th>
                    <th style={th}>הכנסות (הכל)</th>
                    <th style={th}>הכנסות בחודש</th>
                    <th style={th}>סטטוס</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.affiliates || []).map((row) => (
                    <Fragment key={row.affiliate_id}>
                      <tr style={{borderTop: '1px solid #f3f4f6', background: row.paid ? '#f9fafb' : '#fff'}}>
                        <td style={{...td, fontWeight: 600}}>
                          <span
                            onClick={() => toggle(row.affiliate_id)}
                            style={{cursor: 'pointer', color: '#111827'}}
                          >
                            <i className={`bi bi-chevron-${expanded[row.affiliate_id] ? 'down' : 'left'}`} style={{fontSize: 11, marginInlineEnd: 6, color: '#9ca3af'}} />
                            {row.affiliate_name}
                          </span>
                          {row.affiliate_code && <span style={{color: '#9ca3af', fontSize: 12, marginInlineStart: 6}}>({row.affiliate_code})</span>}
                        </td>
                        <td style={{...td, color: '#6b7280', direction: 'ltr', textAlign: 'right'}}>{row.affiliate_phone || '—'}</td>
                        <td style={td}>{row.count}</td>
                        <td style={{...td, fontWeight: 700, color: '#111827'}}>{nis(row.amount)}</td>
                        <td style={{...td, fontWeight: 600, color: row.revenue_total > 0 ? '#1d4ed8' : '#9ca3af'}}>
                          {nis(row.revenue_total || 0)}
                          {row.paying_customers > 0 && (
                            <span style={{color: '#9ca3af', fontWeight: 400, fontSize: 12, marginInlineStart: 5}}>
                              ({row.paying_customers} משלמים)
                            </span>
                          )}
                        </td>
                        <td style={{...td, color: row.revenue_month > 0 ? '#111827' : '#9ca3af'}}>{nis(row.revenue_month || 0)}</td>
                        <td style={td}>
                          {row.paid ? (
                            <span style={{color: '#059669', fontWeight: 600}}>
                              <i className="bi bi-check-circle-fill" style={{marginInlineEnd: 4}} />
                              שולם{row.paid_at ? ` · ${dateLabel(row.paid_at)}` : ''}
                            </span>
                          ) : (
                            <span style={{color: '#d97706', fontWeight: 600}}>ממתין</span>
                          )}
                        </td>
                        <td style={{...td, textAlign: 'left'}}>
                          {row.paid ? (
                            <button
                              className="btn btn-sm btn-light"
                              disabled={saving === row.affiliate_id}
                              onClick={() => doUnmarkPaid(row)}
                              style={{fontSize: 12}}
                            >
                              בטל סימון
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-success"
                              disabled={saving === row.affiliate_id}
                              onClick={() => doMarkPaid(row)}
                              style={{fontSize: 12}}
                            >
                              {saving === row.affiliate_id ? '...' : 'סמן ששולם'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded[row.affiliate_id] && (
                        <tr>
                          <td colSpan={8} style={{padding: 0, background: '#fafafa', borderTop: '1px solid #f3f4f6'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
                              <thead>
                                <tr style={{color: '#9ca3af', textAlign: 'right'}}>
                                  <th style={{...th, paddingInlineStart: 40}}>לקוח</th>
                                  <th style={th}>טלפון</th>
                                  <th style={th}>אימייל</th>
                                  <th style={th}>תאריך חיבור</th>
                                  <th style={th}>שילם עד היום</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.customers.map((c) => (
                                  <tr key={c._id} style={{borderTop: '1px solid #f3f4f6'}}>
                                    <td style={{...td, paddingInlineStart: 40, fontWeight: 500}}>{c.name}</td>
                                    <td style={{...td, color: '#6b7280', direction: 'ltr', textAlign: 'right'}}>{c.phone || '—'}</td>
                                    <td style={{...td, color: '#6b7280'}}>{c.email || '—'}</td>
                                    <td style={{...td, color: '#6b7280'}}>{dateLabel(c.connected_at)}</td>
                                    <td style={{...td, fontWeight: c.revenue > 0 ? 600 : 400, color: c.revenue > 0 ? '#1d4ed8' : '#9ca3af'}}>
                                      {c.revenue > 0 ? nis(c.revenue) : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {(!data || data.affiliates.length === 0) && (
                    <tr><td colSpan={8} style={{...td, textAlign: 'center', color: '#9ca3af', padding: 40}}>אין חיבורי פייסבוק בחודש זה</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{padding: '8px 14px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f3f4f6'}}>
              לחצו על שם אפילייט כדי לראות אילו לקוחות חוברו ומתי.
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const th: React.CSSProperties = {padding: '10px 14px', fontWeight: 600, whiteSpace: 'nowrap'}
const td: React.CSSProperties = {padding: '10px 14px', whiteSpace: 'nowrap'}

export default AffiliatePayoutsPage
